import { Component, ElementRef, OnDestroy, OnInit, ViewChild } from '@angular/core';
import { Subscription } from 'rxjs';
import html2canvas from 'html2canvas';
import { jsPDF } from 'jspdf';
import { ExportDocumentButton } from '../../export-document-button/export-document-button';
import { ExportDownloadModal } from '../export-download-modal/export-download-modal';
import { ExerciseCalendar } from '../shared/exercise-calendar/exercise-calendar';
import { CommissionReportsService } from '../../services/commission-reports.service';
import { SelectedDnService } from '../../services/selected-dn.service';
import { NavalMatrizItem } from '../../interfaces/commission-reports.interface';

interface SimulatorMetricRow {
  label: string;
  newVehicles: string;
  usedVehicles: string;
  total: string;
  inputType?: 'integer' | 'decimal' | 'percentage';
  totalEditable?: boolean;
}

interface NewPeriodRow {
  label: string;
  newVehicles: string;
  usedVehicles: string;
  total: string;
}

interface PenetrationRow {
  label: string;
  values: string[];
}

@Component({
  selector: 'comission-simulator',
  standalone: true,
  imports: [ExerciseCalendar, ExportDocumentButton, ExportDownloadModal],
  templateUrl: './comission-simulator.html',
  styleUrl: './comission-simulator.scss',
})
export class ComissionSimulator implements OnInit, OnDestroy {
  @ViewChild('simulatorContainer') simulatorContainer!: ElementRef<HTMLElement>;

  readonly provisionalDaysPerMonth = 20;
  private readonly regionalMonthlyCommission = 618962;
  private readonly nationalMonthlyCommission = 603913;

  private updateFeedbackTimeout?: ReturnType<typeof setTimeout>;
  private dnSubscription?: Subscription;
  private matrixRequest?: Subscription;

  isEditing = false;
  closingPeriodDraftRows: SimulatorMetricRow[] = [];
  isPenetrationVisible = false;
  isPenetrationLoading = false;
  hasPenetrationData = true;
  isUsingProvisionalMatrix = false;
  regionalAverageDisplay = '—';
  nationalAverageDisplay = '—';
  regionalTrend: { value: string; direction: 'up' | 'down' } | null = null;
  nationalTrend: { value: string; direction: 'up' | 'down' } | null = null;
  lastUpdatedInput = '';
  updateFeedbackMessage = '';
  selectedYear = 2026;
  selectedMonth = 1;
  showExportModal = false;
  exportFileName = '';
  exportFileBlob: Blob | null = null;
  exportEmailSubject = '';
  exportEmailBody = '';
  isExporting = false;

  readonly penetrationColumns = [
    '0-39,9',
    '40-49,9',
    '50-54,9',
    '55-59,9',
    '60-64,9',
    '65-69,9',
    '70-74,9',
    '75-79,9',
    '80-84,9',
    '85-89,9',
    '90-99,9',
    '100-124,9',
    '125-149,9',
    '150-174,9',
    '175-199,9',
    '>=200',
  ];

  readonly penetrationRows: PenetrationRow[] = [
    { label: '>55', values: Array(16).fill('0,00%') },
    { label: '50-54,9', values: Array(16).fill('0,00%') },
    { label: '45-49,9', values: Array(16).fill('0,00%') },
    { label: '40-44,9', values: Array(16).fill('0,00%') },
    { label: '<40', values: Array(16).fill('0,00%') },
  ];

  /**
   * Matriz provisória (valores utilizados na versão anterior do simulador).
   * Aplicada como fallback quando a API naval-matriz não retorna dados para o DN/período,
   * para que o cálculo da comissão continue funcionando de forma provisória.
   */
  private readonly provisionalMatrixFallback: Record<string, string[]> = {
    '>55': ['2,92%', '6,26%', '6,68%', '7,10%', '7,52%', '7,93%', '8,35%', '9,35%', '9,69%', '10,10%', '10,60%', '11,86%', '13,61%', '16,70%', '17,70%', '18,70%'],
    '50-54,9': ['2,21%', '4,73%', '5,04%', '5,36%', '5,67%', '5,99%', '6,30%', '7,06%', '7,31%', '7,62%', '8,00%', '8,95%', '10,27%', '12,60%', '13,36%', '14,11%'],
    '45-49,9': ['2,00%', '4,28%', '4,56%', '4,85%', '5,13%', '5,42%', '5,70%', '6,38%', '6,61%', '6,90%', '7,24%', '8,09%', '9,29%', '11,40%', '12,08%', '12,77%'],
    '40-44,9': ['1,52%', '3,26%', '3,48%', '3,70%', '3,92%', '4,13%', '4,35%', '4,87%', '5,05%', '5,26%', '5,52%', '6,18%', '7,09%', '8,70%', '9,22%', '9,74%'],
    '<40': ['1,24%', '2,66%', '2,84%', '3,02%', '3,20%', '3,37%', '3,55%', '3,98%', '4,12%', '4,30%', '4,51%', '5,04%', '5,79%', '7,10%', '7,53%', '7,95%'],
  };

  readonly realizedRows: SimulatorMetricRow[] = [
    { label: 'Peso de Tabela Médio', newVehicles: '0,6', usedVehicles: '0,8', total: '0,75' },
    { label: 'Contratos/dia útil', newVehicles: '8', usedVehicles: '8', total: '8' },
    { label: 'RMKT', newVehicles: '', usedVehicles: '', total: '' },
    { label: 'Vlr Financiado Médio', newVehicles: 'R$ 83.000', usedVehicles: 'R$ 60.000', total: 'R$ 71.500' },
    { label: 'Vol Agregados ponderado', newVehicles: '', usedVehicles: '', total: '12' },
    { label: 'ISR', newVehicles: '', usedVehicles: '190%', total: '144%' },
    { label: 'Penetration', newVehicles: '', usedVehicles: '', total: '50%' },
    { label: 'Índice', newVehicles: '', usedVehicles: '', total: '10%' },
    { label: 'Comissão/Contrato', newVehicles: 'R$ 3.700', usedVehicles: 'R$ 6.000', total: 'R$ 4.850' },
    { label: 'Comissão/Mês', newVehicles: 'R$ 592.000', usedVehicles: 'R$ 960.000', total: 'R$ 1.552.000' },
  ];

  readonly closingPeriodRows: SimulatorMetricRow[] = [
    { label: 'Contratos/dia útil', newVehicles: '20', usedVehicles: '10', total: '30', inputType: 'integer' },
    { label: 'RMKT', newVehicles: '', usedVehicles: '', total: '' },
    { label: 'Peso Novo', newVehicles: '1', usedVehicles: '1,5', total: '', inputType: 'decimal' },
    { label: 'ISR', newVehicles: '', usedVehicles: '', total: '15', inputType: 'integer', totalEditable: true },
    { label: 'Penetration', newVehicles: '', usedVehicles: '', total: '50', inputType: 'percentage', totalEditable: true },
  ];

  readonly newPeriodRows: NewPeriodRow[] = [
    { label: 'Novo índice', newVehicles: '', usedVehicles: '', total: '8,0%' },
    { label: 'Peso', newVehicles: '1,0', usedVehicles: '1,5', total: '' },
    { label: 'Comissão/Contrato', newVehicles: 'R$ 3.700', usedVehicles: 'R$ 7.500', total: 'R$ 5.600' },
    { label: 'Comissão/Mês', newVehicles: '', usedVehicles: '', total: '' },
    { label: 'Comissão RMKT', newVehicles: '', usedVehicles: '', total: '' },
  ];

  constructor(
    private commissionReportsService: CommissionReportsService,
    private selectedDnService: SelectedDnService
  ) {
    this.recalculateProvisionalResults();
  }

  ngOnInit(): void {
    this.dnSubscription = this.selectedDnService.selectedDn$.subscribe(dn => {
      if (Number(dn) > 0) {
        this.loadPenetrationMatrix();
      }
    });
  }

  ngOnDestroy(): void {
    this.dnSubscription?.unsubscribe();
    this.matrixRequest?.unsubscribe();
  }

  toggleEditing(): void {
    if (!this.isEditing) {
      this.closingPeriodDraftRows = this.closingPeriodRows.map(row => ({ ...row }));
      this.isEditing = true;
      return;
    }

    this.closingPeriodRows.forEach((row, index) => {
      const draft = this.closingPeriodDraftRows[index];
      if (draft) {
        row.newVehicles = draft.newVehicles;
        row.usedVehicles = draft.usedVehicles;
        row.total = draft.total;
      }
    });
    this.isEditing = false;
    this.recalculateProvisionalResults();
    this.updateFeedbackMessage = 'Alterações confirmadas. Os cálculos provisórios foram atualizados.';
    clearTimeout(this.updateFeedbackTimeout);
    this.updateFeedbackTimeout = setTimeout(() => {
      this.lastUpdatedInput = '';
      this.updateFeedbackMessage = '';
    }, 1500);
  }

  togglePenetrationTable(): void {
    this.isPenetrationVisible = !this.isPenetrationVisible;

    if (this.isPenetrationVisible) {
      setTimeout(() => {
        document.getElementById('penetration-table')?.scrollIntoView({
          behavior: 'smooth',
          block: 'nearest',
        });
      });
    }
  }

  onPeriodChange(period: { year: number; month: number }): void {
    this.selectedYear = period.year;
    this.selectedMonth = period.month;
    this.loadPenetrationMatrix();
  }

  private loadPenetrationMatrix(): void {
    const dn = this.selectedDnService.getSelectedDnAsNumber();
    if (!dn) {
      return;
    }

    const exercise = Number(`${this.selectedYear}${String(this.selectedMonth).padStart(2, '0')}`);
    this.matrixRequest?.unsubscribe();
    this.isUsingProvisionalMatrix = false;
    this.penetrationRows.forEach(row => row.values.fill('0,00%'));
    this.recalculateProvisionalResults();
    this.isPenetrationLoading = true;
    this.hasPenetrationData = true;
    this.matrixRequest = this.commissionReportsService.getNavalMatriz(exercise, dn).subscribe({
      next: items => {
        const matchedRows = this.populatePenetrationMatrix(items);
        this.hasPenetrationData = items.length > 0 && matchedRows > 0;
        if (!this.hasPenetrationData) {
          console.warn('Matriz de Penetration sem faixas aplicáveis no simulador.', {
            exercise,
            dn,
            itemCount: items.length,
            sampleRows: items.slice(0, 5).map(item => ({
              tipo: item.tipo,
              penetration: item.penetration,
            })),
          });
          this.applyProvisionalMatrix();
        }
        this.recalculateProvisionalResults();
        this.isPenetrationLoading = false;
      },
      error: error => {
        console.error('Erro ao carregar a matriz Penetration/ISR do simulador:', error);
        this.hasPenetrationData = false;
        this.isPenetrationLoading = false;
        this.applyProvisionalMatrix();
        this.recalculateProvisionalResults();
      }
    });
  }

  private applyProvisionalMatrix(): void {
    this.penetrationRows.forEach(row => {
      const fallbackValues = this.provisionalMatrixFallback[row.label];
      row.values = fallbackValues
        ? [...fallbackValues]
        : Array(this.penetrationColumns.length).fill('0,00%');
    });
    this.isUsingProvisionalMatrix = true;
  }

  private populatePenetrationMatrix(items: NavalMatrizItem[]): number {
    const valueFields: { model: keyof NavalMatrizItem; api: string }[] = [
      { model: 'faixa_0_39_9', api: '0-39,9' },
      { model: 'faixa_40_49_9', api: '40-49,9' },
      { model: 'faixa_50_54_9', api: '50-54,9' },
      { model: 'faixa_55_59_9', api: '55-59,9' },
      { model: 'faixa_60_64_9', api: '60-64,9' },
      { model: 'faixa_65_69_9', api: '65-69,9' },
      { model: 'faixa_70_74_9', api: '70-74,9' },
      { model: 'faixa_75_79_9', api: '75-79,9' },
      { model: 'faixa_80_84_9', api: '80-84,9' },
      { model: 'faixa_85_89_9', api: '85-89,9' },
      { model: 'faixa_90_99_9', api: '90-99,9' },
      { model: 'faixa_100_124_9', api: '100-124,9' },
      { model: 'faixa_125_149_9', api: '125-149,9' },
      { model: 'faixa_150_174_9', api: '150-174,9' },
      { model: 'faixa_175_199_9', api: '175-199,9' },
      { model: 'faixa_Maior_200', api: '>200' },
    ];
    const normalizeRange = (range: string): string =>
      range
        .trim()
        .toLocaleLowerCase()
        .replace(/[‐‑‒–—−]/g, '-')
        .replace(/≥/g, '>=')
        .replace(/≤/g, '<=')
        .replace(/\s/g, '')
        .replace(/%/g, '')
        .replace(/,/g, '.')
        .replace(/(\d+)\.0+(?=$|-)/g, '$1')
        .replace(/(\d+)\.99(?=$|-)/g, '$1.9');
    const getItemValue = (item: NavalMatrizItem, key: string): unknown =>
      (item as unknown as Record<string, unknown>)[key];
    const retailItems = items.filter(item =>
      String(getItemValue(item, 'tipo') ?? getItemValue(item, 'Tipo')).trim().toUpperCase() === 'VAREJO'
    );

    let matchedRows = 0;
    this.penetrationRows.forEach(row => {
      const apiRow = retailItems.find(item => {
        const apiRange = getItemValue(item, 'penetration') ?? getItemValue(item, 'Penetration');
        return apiRange != null && normalizeRange(String(apiRange)) === normalizeRange(row.label);
      });
      if (!apiRow) {
        return;
      }
      matchedRows += 1;

      row.values = valueFields.map(({ model, api }) => {
        const rawValue = getItemValue(apiRow, model) ?? getItemValue(apiRow, api);
        if (rawValue === null || rawValue === undefined || rawValue === '') {
          return '0,00%';
        }
        const normalizedValue = String(rawValue).trim().replace('%', '').replace(',', '.');
        const value = Number(normalizedValue);
        return Number.isFinite(value) ? `${value.toFixed(2).replace('.', ',')}%` : '0,00%';
      });
    });
    return matchedRows;
  }

  onExportRequested(): void {
    const element = this.simulatorContainer?.nativeElement;
    if (!element) {
      console.error('Não foi possível localizar o conteúdo do simulador para exportação.');
      return;
    }

    this.isExporting = true;
    html2canvas(element, {
      scale: 2,
      useCORS: true,
      logging: false,
      backgroundColor: '#ffffff',
      ignoreElements: (candidate) =>
        Boolean(candidate.closest?.('.period-selector, .type-picker-selector, .export-selector')),
    }).then((canvas) => {
      const pdf = new jsPDF({
        orientation: canvas.width > canvas.height ? 'landscape' : 'portrait',
        unit: 'px',
        format: [canvas.width, canvas.height],
      });
      pdf.addImage(canvas.toDataURL('image/png'), 'PNG', 0, 0, canvas.width, canvas.height);

      const period = `${this.selectedYear}-${this.selectedMonth.toString().padStart(2, '0')}`;
      this.exportFileName = `simulador-comissionamento-${period}.pdf`;
      this.exportFileBlob = pdf.output('blob');
      this.exportEmailSubject = `Simulador de Comissionamento - ${period}`;
      this.exportEmailBody = `Segue o Simulador de Comissionamento referente ao período ${this.selectedMonth}/${this.selectedYear}.`;
      this.showExportModal = true;
    }).catch((error: unknown) => {
      console.error('Erro ao gerar PDF do Simulador de Comissionamento:', error);
    }).finally(() => {
      this.isExporting = false;
    });
  }

  handleExportModalDownload(): void {
    if (!this.exportFileBlob || !this.exportFileName) {
      console.error('Não há arquivo disponível para download.');
      this.showExportModal = false;
      return;
    }

    const objectUrl = window.URL.createObjectURL(this.exportFileBlob);
    const anchor = document.createElement('a');
    anchor.href = objectUrl;
    anchor.download = this.exportFileName;
    anchor.style.display = 'none';
    document.body.appendChild(anchor);
    anchor.click();
    document.body.removeChild(anchor);
    window.URL.revokeObjectURL(objectUrl);
    this.showExportModal = false;
  }

  updateClosingInput(
    row: SimulatorMetricRow,
    vehicleType: 'newVehicles' | 'usedVehicles' | 'total',
    event: Event
  ): void {
    const input = event.target as HTMLInputElement;
    const rawValue = input.value;

    if (row.inputType === 'integer') {
      row[vehicleType] = rawValue.replace(/\D/g, '');
    } else {
      const normalizedValue = rawValue.replace(/\./g, ',').replace(/[^\d,]/g, '');
      const [integerPart = '', ...decimalParts] = normalizedValue.split(',');
      const decimalPart = decimalParts.join('').slice(0, 2);
      row[vehicleType] = decimalParts.length > 0
        ? `${integerPart},${decimalPart}`
        : integerPart;
    }

    input.value = row[vehicleType];
    this.lastUpdatedInput = `${row.label}:${vehicleType}`;
    this.updateFeedbackMessage = `Campo ${row.label} alterado. Confirme em OK para atualizar os cálculos.`;
  }

  isInputUpdated(
    row: SimulatorMetricRow,
    vehicleType: 'newVehicles' | 'usedVehicles' | 'total'
  ): boolean {
    return this.lastUpdatedInput === `${row.label}:${vehicleType}`;
  }

  get visibleClosingPeriodRows(): SimulatorMetricRow[] {
    return this.isEditing ? this.closingPeriodDraftRows : this.closingPeriodRows;
  }

  formatCurrency(value: number, fractionDigits = 0): string {
    return new Intl.NumberFormat('pt-BR', {
      style: 'currency',
      currency: 'BRL',
      minimumFractionDigits: fractionDigits,
      maximumFractionDigits: fractionDigits,
    }).format(value);
  }

  get totalCommission(): string {
    return this.newPeriodRows.find(row => row.label === 'Comissão/Mês')?.total ?? this.formatCurrency(0);
  }

  private recalculateProvisionalResults(): void {
    const newContractsPerDay = this.getClosingValue('Contratos/dia útil', 'newVehicles');
    const usedContractsPerDay = this.getClosingValue('Contratos/dia útil', 'usedVehicles');
    const commissionIndex = this.getMatrixCommissionIndex();
    this.setNewPeriodValue('Novo índice', 'total', `${commissionIndex.toFixed(2).replace('.', ',')}%`);
    const index = commissionIndex / 100;
    const newTableWeight = this.getClosingValue('Peso Novo', 'newVehicles');
    const usedTableWeight = this.getClosingValue('Peso Novo', 'usedVehicles');
    const newFinancedAverage = this.getRealizedValue('Vlr Financiado Médio', 'newVehicles');
    const usedFinancedAverage = this.getRealizedValue('Vlr Financiado Médio', 'usedVehicles');
    const newCommissionPerContract = newFinancedAverage * index * newTableWeight;
    const usedCommissionPerContract = usedFinancedAverage * index * usedTableWeight;
    const newMonthlyCommission = newContractsPerDay * this.provisionalDaysPerMonth * newCommissionPerContract;
    const usedMonthlyCommission = usedContractsPerDay * this.provisionalDaysPerMonth * usedCommissionPerContract;

    const weightRow = this.closingPeriodRows.find(row => row.label === 'Peso Novo');
    this.setNewPeriodValue('Peso', 'newVehicles', weightRow?.newVehicles ?? '');
    this.setNewPeriodValue('Peso', 'usedVehicles', weightRow?.usedVehicles ?? '');
    this.setNewPeriodValue('Comissão/Contrato', 'newVehicles', this.formatCurrency(newCommissionPerContract));
    this.setNewPeriodValue('Comissão/Contrato', 'usedVehicles', this.formatCurrency(usedCommissionPerContract));
    this.setNewPeriodValue('Comissão/Contrato', 'total', this.formatCurrency(
      (newCommissionPerContract + usedCommissionPerContract) / 2
    ));
    this.setNewPeriodValue('Comissão/Mês', 'newVehicles', this.formatCurrency(newMonthlyCommission));
    this.setNewPeriodValue('Comissão/Mês', 'usedVehicles', this.formatCurrency(usedMonthlyCommission));
    this.setNewPeriodValue('Comissão/Mês', 'total', this.formatCurrency(newMonthlyCommission + usedMonthlyCommission));

    const contractsRow = this.closingPeriodRows.find(row => row.label === 'Contratos/dia útil');
    if (contractsRow) {
      contractsRow.total = String(newContractsPerDay + usedContractsPerDay);
    }

    this.updateAveragesSummary({
      dealerTotal: newMonthlyCommission + usedMonthlyCommission,
    });
  }

  private updateAveragesSummary(inputs: { dealerTotal: number }): void {
    this.regionalAverageDisplay = this.formatCurrency(this.regionalMonthlyCommission, 2);
    this.nationalAverageDisplay = this.formatCurrency(this.nationalMonthlyCommission, 2);
    this.regionalTrend = this.buildAverageTrend(inputs.dealerTotal, this.regionalMonthlyCommission);
    this.nationalTrend = this.buildAverageTrend(inputs.dealerTotal, this.nationalMonthlyCommission);
  }

  private buildAverageTrend(
    dealerTotal: number,
    average: number | null
  ): { value: string; direction: 'up' | 'down' } | null {
    if (average === null || average <= 0 || dealerTotal <= 0) {
      return null;
    }
    const difference = ((dealerTotal - average) / average) * 100;
    return {
      direction: difference >= 0 ? 'up' : 'down',
      value: `${Math.abs(difference).toFixed(1).replace('.', ',')}%`,
    };
  }

  private getMatrixCommissionIndex(): number {
    const penetration = this.getClosingValue('Penetration', 'total');
    const isr = this.getClosingValue('ISR', 'total');
    return this.getMatrixIndexFor(penetration, isr);
  }

  private getMatrixIndexFor(penetration: number, isr: number): number {
    const rowIndex = this.findRangeIndex(penetration, this.penetrationRows.map(row => row.label));
    const columnIndex = this.findRangeIndex(isr, this.penetrationColumns);
    if (rowIndex < 0 || columnIndex < 0) {
      return 0;
    }

    return this.parseDecimal(this.penetrationRows[rowIndex].values[columnIndex]);
  }

  private findRangeIndex(value: number, ranges: string[]): number {
    return ranges.findIndex(range => {
      const normalized = range.replace(/,/g, '.').replace(/\s/g, '');
      const lowerBound = normalized.match(/^(>=|>)(-?\d+(?:\.\d+)?)$/);
      if (lowerBound) {
        const threshold = Number(lowerBound[2]);
        return lowerBound[1] === '>=' ? value >= threshold : value > threshold;
      }

      const upperBound = normalized.match(/^(<=|<)(-?\d+(?:\.\d+)?)$/);
      if (upperBound) {
        const threshold = Number(upperBound[2]);
        return upperBound[1] === '<=' ? value <= threshold : value < threshold;
      }

      const interval = normalized.match(/^(-?\d+(?:\.\d+)?)-(-?\d+(?:\.\d+)?)$/);
      if (!interval) {
        return false;
      }

      const minimum = Number(interval[1]);
      const maximum = Number(interval[2]);
      return value >= minimum && value < Math.floor(maximum) + 1;
    });
  }

  private parseDecimal(value: string): number {
    const parsed = Number(value.trim().replace('%', '').replace(',', '.'));
    return Number.isFinite(parsed) ? parsed : 0;
  }

  private getClosingValue(
    label: string,
    vehicleType: 'newVehicles' | 'usedVehicles' | 'total'
  ): number {
    const row = this.closingPeriodRows.find(item => item.label === label);
    return row ? this.parseNumber(row[vehicleType]) : 0;
  }

  private getNewPeriodValue(
    label: string,
    vehicleType: 'newVehicles' | 'usedVehicles' | 'total'
  ): number {
    const row = this.newPeriodRows.find(item => item.label === label);
    return row ? this.parseNumber(row[vehicleType]) : 0;
  }

  private getRealizedValue(
    label: string,
    vehicleType: 'newVehicles' | 'usedVehicles'
  ): number {
    const row = this.realizedRows.find(item => item.label === label);
    return row ? this.parseNumber(row[vehicleType]) : 0;
  }

  private setNewPeriodValue(
    label: string,
    field: keyof NewPeriodRow,
    value: string
  ): void {
    const row = this.newPeriodRows.find(item => item.label === label);
    if (row) {
      row[field] = value;
    }
  }

  private parseNumber(value: string): number {
    const normalized = value
      .replace(/[^\d,.-]/g, '')
      .replace(/\./g, '')
      .replace(',', '.');
    const parsed = Number(normalized);
    return Number.isFinite(parsed) ? parsed : 0;
  }

}
